import React, { useEffect, useState } from "react";
import {
  Empty, Input, Select, Spin,
} from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Brush,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dashboardChartTheme from "../../Dashboard/dashboardChartTheme";
import DashboardBrushTraveller from "../../Dashboard/DashboardBrushTraveller";
import {
  collapseOverdueDonutSeries,
} from "../utils/overdueUtils";
import {
  buildRetestCategoryChartSeries,
  buildRetestCustomerChartSeries,
  buildRetestReasonLineSeries,
  RETEST_AGGREGATE_OPTIONS,
  RETEST_DRILL_DOWN_KIND,
} from "../utils/retestUtils";
import "../../Dashboard/DashboardShared.css";
import "../../Dashboard/TestMetrics.css";

/** Categorical palette — same brand anchors as Overdue charts. */
const CHART_SEGMENT_COLORS = [
  dashboardChartTheme.sampleSeries.product,
  dashboardChartTheme.testMetricsSeries.pending,
  dashboardChartTheme.testMetricsSeries.onTime,
  dashboardChartTheme.testMetricsSeries.delayed,
  dashboardChartTheme.sampleSeries.environment,
  dashboardChartTheme.testMetricsSeries.total,
  "#8B6F47",
  "#4A7C8F",
  "#9B6B9E",
  "#5B8A9A",
];

const LINE_SERIES_PALETTE = [
  { color: dashboardChartTheme.sampleSeries.product, backgroundColor: "rgba(83, 85, 147, 0.12)" },
  { color: dashboardChartTheme.testMetricsSeries.pending, backgroundColor: "rgba(112, 121, 200, 0.12)" },
  { color: dashboardChartTheme.testMetricsSeries.onTime, backgroundColor: "rgba(90, 170, 110, 0.12)" },
  { color: dashboardChartTheme.testMetricsSeries.delayed, backgroundColor: "rgba(214, 93, 82, 0.12)" },
  { color: dashboardChartTheme.sampleSeries.environment, backgroundColor: "rgba(92, 154, 122, 0.12)" },
  { color: dashboardChartTheme.testMetricsSeries.total, backgroundColor: "rgba(201, 168, 92, 0.12)" },
  { color: "#8B6F47", backgroundColor: "rgba(139, 111, 71, 0.12)" },
  { color: "#4A7C8F", backgroundColor: "rgba(74, 124, 143, 0.12)" },
  { color: "#9B6B9E", backgroundColor: "rgba(155, 107, 158, 0.12)" },
  { color: "#5B8A9A", backgroundColor: "rgba(91, 138, 154, 0.12)" },
];

/** Donut fills most of the card height; callouts use the remaining outer ring. */
const DONUT_INNER_RADIUS = 72;
const DONUT_OUTER_RADIUS = 110;
const DONUT_CHART_HEIGHT = 300;
const DONUT_LABEL_MAX_CHARS = 12;
const DONUT_MIN_LABEL_PERCENT = 0.04;
const DONUT_RADIAN = Math.PI / 180;

const DEFAULT_VISIBLE_REASON_SERIES = 5;
const REASON_DOT_RADIUS = 4;
const REASON_ACTIVE_DOT_RADIUS = 5;
const REASON_HOVER_STROKE = dashboardChartTheme.sampleSeries.product;
const REASON_CHART_HEIGHT = 360;
const REASON_BRUSH_GUTTER = 20;

/** Compact brush edge labels (full value remains on the chart axis / tooltip). */
const formatBrushTickLabel = (value) => {
  const label = String(value ?? "");
  if (label.length <= 10) {
    return label;
  }
  return `${label.slice(0, 9)}…`;
};

const getChartSegmentColor = (index) => (
  CHART_SEGMENT_COLORS[index % CHART_SEGMENT_COLORS.length]
);

const getReasonSeriesPalette = (reasonIndex) => (
  LINE_SERIES_PALETTE[Math.max(reasonIndex, 0) % LINE_SERIES_PALETTE.length]
);

/** Keep wheel / drag on the tooltip from bubbling into the Recharts canvas. */
const stopTooltipScrollPropagation = (event) => {
  event.stopPropagation();
};

/**
 * Shared Retest chart tooltip shell — header + optional View details + pill rows.
 */
function RetestAnalyticsTooltip({
  active,
  title,
  rows,
  onViewDetails,
}) {
  if (!active || !title) {
    return null;
  }

  const tooltipRows = Array.isArray(rows) ? rows : [];

  const handleViewDetails = (event) => {
    event.stopPropagation();
    if (onViewDetails) {
      onViewDetails();
    }
  };

  return (
    <div
      className="DataOpsAnalytics__ChartTooltip"
      onMouseDown={stopTooltipScrollPropagation}
      onTouchStart={stopTooltipScrollPropagation}
    >
      <div className="DataOpsAnalytics__ChartTooltipHeader">
        <span className="DataOpsAnalytics__ChartTooltipTitle">{title}</span>
        {onViewDetails ? (
          <button
            type="button"
            className="DataOpsAnalytics__ChartTooltipViewDetails"
            onClick={handleViewDetails}
          >
            View details
          </button>
        ) : null}
      </div>
      <div
        className="DataOpsAnalytics__ChartTooltipList"
        onWheel={stopTooltipScrollPropagation}
        onScroll={stopTooltipScrollPropagation}
      >
        {tooltipRows.map((row) => (
          <div key={row.key} className="DataOpsAnalytics__ChartTooltipRow">
            <div className="DataOpsAnalytics__ChartTooltipRowLabel">
              <span
                className="DataOpsAnalytics__ChartTooltipDot"
                style={{ backgroundColor: row.color }}
              />
              <span>{row.label}</span>
            </div>
            <span className="DataOpsAnalytics__ChartTooltipValue">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const CHART_TOOLTIP_WRAPPER_STYLE = {
  pointerEvents: "auto",
  zIndex: 20,
};

function DonutChartTooltip({ active, payload, onViewDetails }) {
  const point = payload?.[0]?.payload;
  if (!active || !point) {
    return null;
  }

  const countEntry = payload.find((entry) => entry.dataKey === "count") || payload[0];
  const countColor = countEntry?.color || CHART_SEGMENT_COLORS[0];
  const breakdownItems = point.otherBreakdown?.length
    ? point.otherBreakdown
    : (point.reasonBreakdown || []);

  const tooltipRows = [
    {
      key: `${point.key}-count`,
      label: "Count",
      value: String(point.count ?? 0),
      color: countColor,
    },
    ...breakdownItems.map((item, index) => ({
      key: `${point.key}-${item.key || item.name}-${index}`,
      label: item.name,
      value: String(item.count ?? 0),
      color: getChartSegmentColor(index),
    })),
  ];

  return (
    <RetestAnalyticsTooltip
      active={active}
      title={point.name}
      rows={tooltipRows}
      onViewDetails={
        onViewDetails
          ? () => onViewDetails(point)
          : undefined
      }
    />
  );
}

const truncateDonutLabel = (name) => {
  const safeName = String(name ?? "");
  if (safeName.length <= DONUT_LABEL_MAX_CHARS) {
    return safeName;
  }
  return `${safeName.slice(0, DONUT_LABEL_MAX_CHARS - 1)}…`;
};

function CategoryDonutLabel({
  cx,
  cy,
  midAngle,
  outerRadius,
  name,
  percent,
  showLabel,
}) {
  if (!showLabel || percent < DONUT_MIN_LABEL_PERCENT) {
    return null;
  }

  const radius = outerRadius + 16;
  const x = cx + radius * Math.cos(-midAngle * DONUT_RADIAN);
  const y = cy + radius * Math.sin(-midAngle * DONUT_RADIAN);

  return (
    <text
      x={x}
      y={y}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      className="DataOpsAnalytics__DonutLabel"
    >
      {truncateDonutLabel(name)}
    </text>
  );
}

function CategoryDonutLabelLine({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  showLabel,
}) {
  if (!showLabel || percent < DONUT_MIN_LABEL_PERCENT) {
    return null;
  }

  const startRadius = outerRadius + 2;
  const endRadius = outerRadius + 14;
  const startX = cx + startRadius * Math.cos(-midAngle * DONUT_RADIAN);
  const startY = cy + startRadius * Math.sin(-midAngle * DONUT_RADIAN);
  const endX = cx + endRadius * Math.cos(-midAngle * DONUT_RADIAN);
  const endY = cy + endRadius * Math.sin(-midAngle * DONUT_RADIAN);

  return (
    <path
      d={`M${startX},${startY}L${endX},${endY}`}
      stroke="#bfbfbf"
      fill="none"
      strokeWidth={1}
    />
  );
}

function ReasonTrendCursor({ points, height }) {
  if (!points?.length) {
    return null;
  }
  const { x } = points[0];
  return (
    <line
      x1={x}
      y1={0}
      x2={x}
      y2={height}
      stroke={REASON_HOVER_STROKE}
      strokeWidth={1}
      strokeDasharray="4 4"
    />
  );
}

function ReasonTrendTooltip({
  active,
  payload,
  seriesColorByKey,
  onViewDetails,
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload;
  if (!point) {
    return null;
  }

  const tooltipRows = [...payload]
    .filter((entry) => entry.value != null)
    .sort((left, right) => Number(right.value) - Number(left.value))
    .map((entry) => ({
      key: entry.dataKey,
      label: entry.name,
      value: String(entry.value ?? 0),
      color: seriesColorByKey?.[entry.dataKey]?.color || entry.color,
    }));

  return (
    <RetestAnalyticsTooltip
      active={active}
      title={point.periodLabel}
      rows={tooltipRows}
      onViewDetails={
        onViewDetails
          ? () => onViewDetails(point)
          : undefined
      }
    />
  );
}

function ChartEmptyState({ title, description }) {
  return (
    <div className="DataOpsAnalytics__ChartEmpty">
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={description || `No data for ${title}`}
      />
    </div>
  );
}

function RetestDonutChart({ series, emptyTitle, onSliceClick }) {
  const donutSeries = collapseOverdueDonutSeries(series);

  if (!donutSeries.length) {
    return <ChartEmptyState title={emptyTitle} />;
  }

  return (
    <div className="DataOpsAnalytics__DonutChart">
      <ResponsiveContainer width="100%" height={DONUT_CHART_HEIGHT}>
        <PieChart
          margin={{
            top: 8,
            right: 8,
            bottom: 8,
            left: 8,
          }}
        >
          <Pie
            data={donutSeries}
            dataKey="count"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={DONUT_INNER_RADIUS}
            outerRadius={DONUT_OUTER_RADIUS}
            paddingAngle={1}
            label={CategoryDonutLabel}
            labelLine={CategoryDonutLabelLine}
            cursor={onSliceClick ? "pointer" : "default"}
            onClick={(point) => {
              if (!onSliceClick || !point) {
                return;
              }
              onSliceClick(point.payload || point);
            }}
          >
            {donutSeries.map((point, index) => (
              <Cell
                key={point.key}
                fill={getChartSegmentColor(index)}
              />
            ))}
          </Pie>
          <Tooltip
            wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
            content={(tooltipProps) => (
              <DonutChartTooltip
                active={tooltipProps.active}
                payload={tooltipProps.payload}
                onViewDetails={onSliceClick}
              />
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function ReasonSeriesPill({
  label,
  color,
  backgroundColor,
  isSelected,
  onToggle,
}) {
  return (
    <button
      type="button"
      className={
        isSelected
          ? "DataOpsAnalytics__SlaPill DataOpsAnalytics__SlaPill--active"
          : "DataOpsAnalytics__SlaPill DataOpsAnalytics__SlaPill--inactive"
      }
      style={isSelected ? { borderColor: color, color, backgroundColor } : undefined}
      onClick={onToggle}
      aria-pressed={isSelected}
    >
      <span className="DataOpsAnalytics__SlaPillLabel">{label}</span>
      {isSelected
        ? <CloseOutlined className="DataOpsAnalytics__SlaPillIcon" aria-hidden />
        : <PlusOutlined className="DataOpsAnalytics__SlaPillIcon" aria-hidden />}
    </button>
  );
}

function ReasonTrendDot({
  cx,
  cy,
  stroke,
  value,
  payload,
  dataKey,
  onDotClick,
  radius = REASON_DOT_RADIUS,
}) {
  if (value == null || cx == null || cy == null) {
    return null;
  }

  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      fill="#ffffff"
      stroke={stroke}
      strokeWidth={2}
      className={
        onDotClick
          ? "DataOpsAnalytics__SlaDot DataOpsAnalytics__SlaDot--clickable"
          : "DataOpsAnalytics__SlaDot"
      }
      onClick={(event) => {
        if (!onDotClick) {
          return;
        }
        event.stopPropagation();
        onDotClick(payload, dataKey);
      }}
    />
  );
}

/**
 * Multi-line retest volume by reason — pills + brush aligned with Overdue SLA trend UX.
 * Dot / View details opens reason+period drill-down.
 */
function ReasonTrendChart({
  rows,
  aggregate,
  onDrillDown,
}) {
  const [selectedSeriesKeys, setSelectedSeriesKeys] = useState([]);
  const [seriesSearchText, setSeriesSearchText] = useState("");

  const { chartRows, lineDescriptors } = buildRetestReasonLineSeries(
    rows,
    aggregate,
  );
  const seriesSignature = lineDescriptors.map((descriptor) => descriptor.dataKey).join("|");

  useEffect(() => {
    setSelectedSeriesKeys(
      lineDescriptors
        .slice(0, DEFAULT_VISIBLE_REASON_SERIES)
        .map((descriptor) => descriptor.dataKey),
    );
    setSeriesSearchText("");
  }, [seriesSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!chartRows.length) {
    return <ChartEmptyState title="retest volume by reason" />;
  }

  const seriesColorByKey = {};
  lineDescriptors.forEach((descriptor) => {
    seriesColorByKey[descriptor.dataKey] = getReasonSeriesPalette(descriptor.reasonIndex);
  });

  const normalizedSeriesSearch = seriesSearchText.trim().toLowerCase();
  const filteredLineDescriptors = normalizedSeriesSearch
    ? lineDescriptors.filter((descriptor) => (
      descriptor.legendName.toLowerCase().includes(normalizedSeriesSearch)
    ))
    : lineDescriptors;

  const visibleDescriptors = lineDescriptors.filter((descriptor) => (
    selectedSeriesKeys.includes(descriptor.dataKey)
  ));

  const toggleSeries = (dataKey) => {
    setSelectedSeriesKeys((previousKeys) => (
      previousKeys.includes(dataKey)
        ? previousKeys.filter((key) => key !== dataKey)
        : [...previousKeys, dataKey]
    ));
  };

  const openReasonPeriodDrillDown = (periodPoint, reasonKey) => {
    if (!onDrillDown || !periodPoint || !reasonKey) {
      return;
    }

    const seriesDescriptor = lineDescriptors.find(
      (descriptor) => descriptor.dataKey === reasonKey,
    );
    const seriesLabel = seriesDescriptor?.legendName || "Reason";
    const countValue = periodPoint[reasonKey] ?? 0;
    const periodRangeLabel = periodPoint.periodStart && periodPoint.periodEnd
      ? `${periodPoint.periodStart} – ${periodPoint.periodEnd}`
      : periodPoint.periodLabel;

    onDrillDown({
      kind: RETEST_DRILL_DOWN_KIND.REASON_PERIOD,
      seriesKey: reasonKey,
      periodStart: periodPoint.periodStart,
      periodEnd: periodPoint.periodEnd,
      title: `${seriesLabel} — ${periodPoint.periodLabel}`,
      subtitle: `Count: ${countValue} · Period ${periodRangeLabel}`,
    });
  };

  const openPeriodOverviewDrillDown = (periodPoint) => {
    if (!onDrillDown || !periodPoint) {
      return;
    }

    const periodRangeLabel = periodPoint.periodStart && periodPoint.periodEnd
      ? `${periodPoint.periodStart} – ${periodPoint.periodEnd}`
      : periodPoint.periodLabel;

    onDrillDown({
      kind: RETEST_DRILL_DOWN_KIND.REASON_PERIOD,
      periodStart: periodPoint.periodStart,
      periodEnd: periodPoint.periodEnd,
      title: `Retests — ${periodPoint.periodLabel}`,
      subtitle: `All reasons · Period ${periodRangeLabel}`,
    });
  };

  return (
    <div className="DataOpsAnalytics__SlaTrend">
      <div className="DataOpsAnalytics__SlaSeriesControls">
        <Input.Search
          allowClear
          className="DataOpsAnalytics__Search DataOpsAnalytics__SlaSeriesSearch"
          placeholder="Search reasons"
          value={seriesSearchText}
          onChange={(event) => setSeriesSearchText(event.target.value)}
        />
        <div className="DataOpsAnalytics__SlaPills">
          {filteredLineDescriptors.length === 0 ? (
            <span className="DataOpsAnalytics__SlaSeriesEmpty">
              No reasons match your search.
            </span>
          ) : (
            filteredLineDescriptors.map((descriptor) => {
              const palette = seriesColorByKey[descriptor.dataKey];
              return (
                <ReasonSeriesPill
                  key={descriptor.dataKey}
                  label={descriptor.legendName}
                  color={palette.color}
                  backgroundColor={palette.backgroundColor}
                  isSelected={selectedSeriesKeys.includes(descriptor.dataKey)}
                  onToggle={() => toggleSeries(descriptor.dataKey)}
                />
              );
            })
          )}
        </div>
      </div>

      {visibleDescriptors.length === 0 ? (
        <div className="DataOpsAnalytics__SlaPlaceholder" role="alert">
          Please select at least one series to display the graph.
        </div>
      ) : (
        <div className="DataOpsAnalytics__SlaChartWrapper">
          <div className="DataOpsAnalytics__SlaChartArea">
            <ResponsiveContainer width="100%" height={REASON_CHART_HEIGHT}>
              <LineChart
                data={chartRows}
                margin={{
                  top: 10,
                  right: 16 + REASON_BRUSH_GUTTER,
                  left: REASON_BRUSH_GUTTER,
                  bottom: 10,
                }}
              >
                <CartesianGrid
                  strokeDasharray="4 4"
                  stroke={dashboardChartTheme.gridStroke}
                  vertical
                  horizontal
                />
                <XAxis
                  dataKey="periodLabel"
                  tickLine={false}
                  axisLine={false}
                  tick={{
                    fill: "#6b778c",
                    fontSize: 11,
                    fontFamily: "Roboto",
                  }}
                  interval="preserveStartEnd"
                  padding={{ left: 10, right: 10 }}
                  height={36}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  allowDecimals={false}
                  tick={{
                    fill: "#6b778c",
                    fontSize: 11,
                    fontFamily: "Roboto",
                  }}
                  domain={[0, "auto"]}
                />
                <Tooltip
                  wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
                  content={(tooltipProps) => (
                    <ReasonTrendTooltip
                      active={tooltipProps.active}
                      payload={tooltipProps.payload}
                      seriesColorByKey={seriesColorByKey}
                      onViewDetails={
                        onDrillDown
                          ? openPeriodOverviewDrillDown
                          : undefined
                      }
                    />
                  )}
                  cursor={<ReasonTrendCursor />}
                />
                {visibleDescriptors.map((descriptor) => {
                  const stroke = seriesColorByKey[descriptor.dataKey].color;
                  return (
                    <Line
                      key={descriptor.dataKey}
                      type={dashboardChartTheme.line.curveType}
                      dataKey={descriptor.dataKey}
                      name={descriptor.legendName}
                      stroke={stroke}
                      strokeWidth={dashboardChartTheme.line.strokeWidth}
                      strokeLinecap={dashboardChartTheme.line.strokeLinecap}
                      strokeLinejoin={dashboardChartTheme.line.strokeLinejoin}
                      connectNulls
                      isAnimationActive={false}
                      dot={(dotProps) => (
                        <ReasonTrendDot
                          cx={dotProps.cx}
                          cy={dotProps.cy}
                          value={dotProps.value}
                          stroke={stroke}
                          payload={dotProps.payload}
                          dataKey={descriptor.dataKey}
                          onDotClick={onDrillDown ? openReasonPeriodDrillDown : undefined}
                        />
                      )}
                      activeDot={(dotProps) => (
                        <ReasonTrendDot
                          cx={dotProps.cx}
                          cy={dotProps.cy}
                          value={dotProps.value}
                          stroke={REASON_HOVER_STROKE}
                          radius={REASON_ACTIVE_DOT_RADIUS}
                          payload={dotProps.payload}
                          dataKey={descriptor.dataKey}
                          onDotClick={onDrillDown ? openReasonPeriodDrillDown : undefined}
                        />
                      )}
                    />
                  );
                })}
                <Brush
                  dataKey="periodLabel"
                  stroke={dashboardChartTheme.brush.stroke}
                  fill={dashboardChartTheme.brush.fill}
                  height={dashboardChartTheme.brush.height}
                  travellerWidth={dashboardChartTheme.brush.travellerWidth}
                  traveller={DashboardBrushTraveller}
                  tickFormatter={formatBrushTickLabel}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function ChartCard({
  title, headerExtra, fullWidth = false, children,
}) {
  return (
    <div
      className={
        fullWidth
          ? "TestMetrics__ChartCard DataOpsAnalytics__ChartCard DataOpsAnalytics__ChartCard--fullWidth"
          : "TestMetrics__ChartCard DataOpsAnalytics__ChartCard"
      }
    >
      <div className="TestMetrics__ChartHeader DataOpsAnalytics__ChartHeader">
        <h3>{title}</h3>
        {headerExtra}
      </div>
      {children}
    </div>
  );
}

function ChartsLoading() {
  return (
    <div className="DataOpsAnalytics__ChartEmpty">
      <Spin tip="Loading charts..." />
    </div>
  );
}

/**
 * Retest charts — reason volume full width; customer + category donuts on one row.
 * Charts use retest_traceability list rows. Customer labels prefer
 * row.customer_company, then the customers dropdown catalog. Customer PKs
 * still come from that catalog. Category names come from the category catalog
 * when test_category_id is present. Parent gates drill-down when ids / Other are missing.
 */
export default function RetestCharts({
  rows,
  loading,
  reasonAggregate,
  onReasonAggregateChange,
  onDrillDown,
  customerLookup = null,
  categoryLabelById = null,
}) {
  const customerSeries = buildRetestCustomerChartSeries(rows, customerLookup);
  const categorySeries = buildRetestCategoryChartSeries(rows, categoryLabelById);
  const hasRows = Array.isArray(rows) && rows.length > 0;
  const chartsLoading = loading && !hasRows;

  const handleDonutSliceClick = (kind, point) => {
    if (!onDrillDown || !point?.key) {
      return;
    }

    onDrillDown({
      kind,
      seriesKey: point.key,
      testCategoryId: point.testCategoryId ?? null,
      customerPk: point.customerPk ?? null,
      title: point.name || (kind === RETEST_DRILL_DOWN_KIND.CUSTOMER ? "Customer" : "Category"),
      subtitle: `Count: ${point.count ?? 0}`,
    });
  };

  return (
    <div className="DataOpsAnalytics__ChartsStack">
      <ChartCard
        title="Retest volume by reason"
        fullWidth
        headerExtra={(
          <div className="TestMetrics__FrequencyDropdown">
            <Select
              value={reasonAggregate}
              onChange={onReasonAggregateChange}
              options={RETEST_AGGREGATE_OPTIONS}
            />
          </div>
        )}
      >
        {chartsLoading
          ? <ChartsLoading />
          : (
            <ReasonTrendChart
              rows={rows}
              aggregate={reasonAggregate}
              onDrillDown={onDrillDown}
            />
          )}
      </ChartCard>

      <ChartCard title="Retest by customer">
        {chartsLoading
          ? <ChartsLoading />
          : (
            <RetestDonutChart
              series={customerSeries}
              emptyTitle="customer distribution"
              onSliceClick={
                onDrillDown
                  ? (point) => handleDonutSliceClick(RETEST_DRILL_DOWN_KIND.CUSTOMER, point)
                  : undefined
              }
            />
          )}
      </ChartCard>

      <ChartCard title="Retest by test category">
        {chartsLoading
          ? <ChartsLoading />
          : (
            <RetestDonutChart
              series={categorySeries}
              emptyTitle="category distribution"
              onSliceClick={
                onDrillDown
                  ? (point) => handleDonutSliceClick(RETEST_DRILL_DOWN_KIND.CATEGORY, point)
                  : undefined
              }
            />
          )}
      </ChartCard>
    </div>
  );
}
